<template>
  <div class="text-props" :class="{ 'text-props--disabled': disabled }">
    <SectionHeader title="文本属性" />

    <!-- Text content input -->
    <div class="text-props__content-box">
      <textarea
        class="text-props__content-input"
        :value="text"
        :placeholder="TEXT_CONTENT_PLACEHOLDER"
        :maxlength="TEXT_MAX_LENGTH"
        rows="3"
        @input="onTextInput($event)"
      ></textarea>
      <div class="text-props__content-footer">
        <span class="text-props__content-counter">{{ textLength }}/{{ TEXT_MAX_LENGTH }}</span>
      </div>
    </div>

    <!-- Font selector -->
    <div class="text-props__font-row">
      <FontSelector v-model="fontFamily" />
    </div>

    <!-- Font size row: color label + font size + +/- -->
    <div class="text-props__tool-row">
      <ColorPickerPopover v-model="fillColorModel" placement="bottom-end">
        <div
          class="text-props__color-swatch"
          :style="{ '--swatch-color': fillColorValue }"
          title="文字颜色"
        ></div>
      </ColorPickerPopover>

      <el-select
        class="text-props__font-size"
        popper-class="text-props__font-size-popper"
        :model-value="fontSizeModel"
        filterable
        allow-create
        @update:model-value="onFontSizeInput"
      >
        <el-option
          v-for="size in fontSizeOptions"
          :key="size"
          :label="String(size)"
          :value="size"
        />
      </el-select>

      <div class="text-props__step-controls">
        <div class="text-props__step-divider"></div>
        <button class="text-props__step-btn" @click="onFontSizeChange(-1)">
          <SvgIcon name="editor-minus" :size="11" color="#64748B" />
        </button>
        <button class="text-props__step-btn" @click="onFontSizeChange(1)">
          <SvgIcon name="editor-plus" :size="11" color="#64748B" />
        </button>
      </div>
    </div>

    <!-- Alignment buttons row -->
    <div class="text-props__align-row">
      <div class="text-props__align-group">
        <button
          v-for="style in fontStyleOptions"
          :key="style.value"
          class="text-props__align-btn"
          :class="{ 'text-props__align-btn--active': isFontStyleActive(style.value) }"
          @click="onFontStyleClick(style.value)"
        >
          <SvgIcon
            :name="style.icon"
            :size="11"
            :color="isFontStyleActive(style.value) ? '#6033E1' : '#64748B'"
          />
        </button>
      </div>
      <div class="text-props__align-group">
        <button
          v-for="align in alignOptions"
          :key="align.value"
          class="text-props__align-btn"
          :class="{
            'text-props__align-btn--active': (textStyle?.layout?.align || 'left') === align.value
          }"
          @click="onAlignClick(align.value)"
        >
          <SvgIcon
            :name="align.icon"
            :size="11"
            :color="(textStyle?.layout?.align || 'left') === align.value ? '#6033E1' : '#64748B'"
          />
        </button>
      </div>
    </div>

    <!-- Divider -->
    <div class="text-props__sep"></div>

    <!-- Line spacing -->
    <div class="text-props__spacing-row">
      <span class="text-props__label">行间距</span>
      <div class="text-props__slider-wrap">
        <input
          type="range"
          class="text-props__slider"
          :min="0"
          :max="50"
          :step="0.1"
          :value="textStyle?.layout?.leading ?? 0"
          @input="onLeadingChange(($event.target as HTMLInputElement).value)"
        />
      </div>
      <input
        type="number"
        class="text-props__spacing-val"
        :value="textStyle?.layout?.leading ?? 0"
        min="0"
        max="50"
        step="0.1"
        @input="onLeadingInput(($event.target as HTMLInputElement).value)"
        @blur="onLeadingBlur($event)"
      />
    </div>

    <!-- Character spacing -->
    <div class="text-props__spacing-row">
      <span class="text-props__label">字间距</span>
      <div class="text-props__slider-wrap">
        <input
          type="range"
          class="text-props__slider"
          :min="0"
          :max="50"
          :step="0.1"
          :value="textStyle?.layout?.letterSpacing ?? 0"
          @input="onLetterSpacingChange(($event.target as HTMLInputElement).value)"
        />
      </div>
      <input
        type="number"
        class="text-props__spacing-val"
        :value="textStyle?.layout?.letterSpacing ?? 0"
        min="0"
        max="50"
        step="0.1"
        @input="onLetterSpacingInput(($event.target as HTMLInputElement).value)"
        @blur="onLetterSpacingBlur($event)"
      />
    </div>

    <!-- Divider -->
    <div class="text-props__sep"></div>

    <!-- Style preview area -->
    <StyleSelectPopover placement="bottom-start" @select="onStyleSelect">
      <div class="text-props__preview">
        <div class="text-props__preview-left">
          <div class="text-props__preview-char">
            <span class="text-props__big-a">A</span>
          </div>
          <span class="text-props__preview-label">选择样式</span>
        </div>
        <span class="text-props__preview-chevron">
          <SvgIcon name="editor-chevron-right" :size="15" color="#9CA3AF" />
        </span>
      </div>
    </StyleSelectPopover>

    <!-- Fill color -->
    <div class="text-props__color-row">
      <span class="text-props__color-label">填充颜色</span>
      <ColorPickerPopover v-model="fillColorModel" placement="bottom-end">
        <div class="text-props__color-option">
          <div class="text-props__gradient-preview" :style="fillPreviewStyle"></div>
          <span>{{ fillLabel }}</span>
        </div>
      </ColorPickerPopover>
    </div>

    <!-- Stroke -->
    <div class="text-props__color-row">
      <span class="text-props__color-label">描边颜色</span>
      <div class="text-props__stroke-group">
        <ColorPickerPopover v-model="strokeColorModel" placement="bottom-end">
          <div class="text-props__stroke-color-box">
            <div class="text-props__color-dot" :style="{ backgroundColor: strokeColorModel }"></div>
            <span>{{ strokeColorModel }}</span>
          </div>
        </ColorPickerPopover>
        <div class="text-props__shadow-num-box">
          <input
            type="number"
            class="text-props__shadow-num-input"
            :value="strokeAlpha"
            min="0"
            max="100"
            @input="onStrokeAlphaInput(($event.target as HTMLInputElement).value)"
          />
          <span class="text-props__shadow-num-unit">%</span>
        </div>
        <div class="text-props__stroke-width-box">
          <SvgIcon
            name="editor-stroke-width"
            :size="12"
            color="#616569"
            class="text-props__scrub-icon"
            @mousedown="strokeWidthScrub.onMouseDown($event, strokeWidth)"
          />
          <input
            type="number"
            class="text-props__stroke-width-input"
            :value="strokeWidth"
            min="0"
            max="20"
            step="0.5"
            @input="onStrokeWidthInput(($event.target as HTMLInputElement).value)"
            @blur="onStrokeWidthBlur($event)"
          />
        </div>
      </div>
    </div>

    <!-- Divider -->
    <div class="text-props__sep"></div>

    <!-- Shadow header with toggle -->
    <div class="text-props__shadow-header">
      <span class="text-props__shadow-label">阴影</span>
      <button
        class="text-props__toggle"
        :class="{ 'text-props__toggle--on': shadowEnabled }"
        @click="toggleShadow"
      ></button>
    </div>

    <!-- Shadow settings -->
    <div v-if="shadowEnabled" class="text-props__shadow-box">
      <!-- Shadow color + alpha -->
      <div class="text-props__shadow-row">
        <span class="text-props__shadow-label">颜色</span>
        <div class="text-props__shadow-row-right">
          <ColorPickerPopover v-model="shadowColorModel" placement="bottom-end">
            <div class="text-props__shadow-color-box">
              <div
                class="text-props__color-dot"
                :style="{ backgroundColor: shadowColorModel }"
              ></div>
              <span>{{ shadowColorModel.substring(0, 7) }}</span>
            </div>
          </ColorPickerPopover>
          <div class="text-props__shadow-num-box">
            <input
              type="number"
              class="text-props__shadow-num-input"
              :value="Math.round((props.textStyle?.shadow?.alpha ?? 0.5) * 100)"
              min="0"
              max="100"
              @input="onShadowAlphaInput(($event.target as HTMLInputElement).value)"
            />
            <span class="text-props__shadow-num-unit">%</span>
          </div>
        </div>
      </div>

      <!-- Shadow distance -->
      <div class="text-props__shadow-row">
        <span class="text-props__shadow-label">距离</span>
        <div class="text-props__shadow-row-right">
          <div class="text-props__shadow-num-box">
            <SvgIcon
              name="editor-shadow-distance"
              :size="14"
              color="#616569"
              class="text-props__shadow-num-icon"
              @mousedown="shadowDistanceScrub.onMouseDown($event, shadowDistance)"
            />
            <input
              type="number"
              class="text-props__shadow-num-input"
              :value="shadowDistance"
              min="0"
              max="50"
              step="1"
              @input="onShadowDistanceInput(($event.target as HTMLInputElement).value)"
              @blur="onShadowDistanceBlur($event)"
            />
          </div>
        </div>
      </div>

      <!-- Shadow angle -->
      <div class="text-props__shadow-row">
        <span class="text-props__shadow-label">角度</span>
        <div class="text-props__shadow-row-right">
          <div class="text-props__shadow-num-box">
            <SvgIcon
              name="editor-shadow-param"
              :size="14"
              color="#616569"
              class="text-props__shadow-num-icon"
              @mousedown="shadowAngleScrub.onMouseDown($event, shadowAngle)"
            />
            <input
              type="number"
              class="text-props__shadow-num-input"
              :value="shadowAngle"
              min="0"
              max="360"
              step="1"
              @input="onShadowAngleInput(($event.target as HTMLInputElement).value)"
              @blur="onShadowAngleBlur($event)"
            />
          </div>
        </div>
      </div>

      <!-- Shadow blur -->
      <div class="text-props__shadow-row">
        <span class="text-props__shadow-label">模糊</span>
        <div class="text-props__shadow-row-right">
          <div class="text-props__shadow-num-box">
            <SvgIcon
              name="editor-shadow-blur"
              :size="12"
              color="#616569"
              class="text-props__shadow-num-icon"
              @mousedown="shadowBlurScrub.onMouseDown($event, shadowBlur)"
            />
            <input
              type="number"
              class="text-props__shadow-num-input"
              :value="shadowBlur"
              min="0"
              max="50"
              step="0.5"
              @input="onShadowBlurInput(($event.target as HTMLInputElement).value)"
              @blur="onShadowBlurBlur($event)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Apply to all pages button (hidden on cover page) -->
    <button v-if="!hideApplyAll" class="text-props__apply-btn" @click="$emit('apply-to-all')">
      <SvgIcon name="editor-apply-all" :size="18" />
      <span>当前样式应用到所有页面</span>
    </button>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import ColorPickerPopover from '@/components/common/color/ColorPickerPopover.vue';
  import StyleSelectPopover from '../tool/StyleSelectPopover.vue';
  import type { StylePreset } from '../tool/StyleSelectPopover.vue';
  import FontSelector from '../tool/font/FontSelector.vue';
  import type { TextStyleData } from '@/types/pixiText-type';
  import { toHexColor } from '@/utils';
  import SectionHeader from './SectionHeader.vue';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';

  /** PixiJS v8 TextStyle 默认字号 */
  const DEFAULT_FONT_SIZE = 26;
  const FONT_SIZE_MIN = 12;
  const FONT_SIZE_MAX = 200;
  const FONT_SIZE_STEP = 2;
  const TEXT_CONTENT_PLACEHOLDER = '在此输入文本内容';
  const TEXT_MAX_LENGTH = 100;

  /** 字号下拉选项：12, 14, 16, ..., 200 */
  const fontSizeOptions = computed<number[]>(() => {
    const options: number[] = [];
    for (let i = FONT_SIZE_MIN; i <= FONT_SIZE_MAX; i += FONT_SIZE_STEP) {
      options.push(i);
    }
    return options;
  });

  const props = defineProps<{
    textStyle?: TextStyleData;
    text?: string;
    disabled?: boolean;
    hideApplyAll?: boolean;
  }>();

  const textLength = computed(() => props.text?.length ?? 0);

  /** 字号 v-model */
  const fontSizeModel = computed<number>({
    get: () => props.textStyle?.font?.fontSize ?? DEFAULT_FONT_SIZE,
    set: (val) => emit('update:textStyle', { font: { fontSize: val } })
  });

  const emit = defineEmits<{
    'update:textStyle': [value: TextStyleData];
    'update:text': [value: string];
    'toggle-style': [];
    'select-fill-color': [];
    'select-stroke-color': [];
    'apply-to-all': [];
  }>();

  /** 字体家族 v-model，桥接 FontSelector 到 update:textStyle */
  const fontFamily = computed<string>({
    get: () => (props.textStyle?.font?.fontFamily as string) || '',
    set: (val) => emit('update:textStyle', { font: { fontFamily: val } })
  });

  const alignOptions = [
    { value: 'left' as const, icon: 'editor-align-left' },
    { value: 'center' as const, icon: 'editor-align-center' },
    { value: 'right' as const, icon: 'editor-align-right' },
    { value: 'justify' as const, icon: 'editor-align-justify' }
  ];

  const fontStyleOptions = [
    { value: 'bold' as const, icon: 'editor-bold' },
    { value: 'italic' as const, icon: 'editor-italic' }
  ];

  /** 从 TextFillData 中提取首色（处理纯色 / 渐变） */
  function getFillHex(): string {
    const fill = props.textStyle?.fill;
    if (!fill) return '#000000';
    if (typeof fill.fill === 'string') return toHexColor(fill.fill);
    if (fill.fill.length > 0) return toHexColor(fill.fill[0]);
    return '#000000';
  }

  const fillColorValue = computed(() => getFillHex());

  const fillLabel = computed(() => {
    const fill = props.textStyle?.fill;
    if (!fill) return '线性渐变';
    return fill.type === 'linear' ? '线性渐变' : fill.type === 'radial' ? '径向渐变' : '纯色';
  });

  const fillPreviewStyle = computed(() => {
    const fill = props.textStyle?.fill;
    if (!fill)
      return {
        background: 'linear-gradient(180deg, rgba(218, 0, 39, 1) 0%, rgba(255, 183, 167, 1) 100%)'
      };
    return { backgroundColor: getFillHex() };
  });

  /** 填充颜色 v-model */
  const fillColorModel = computed<string>({
    get: () => fillColorValue.value,
    set: (val) => {
      if (val) emit('update:textStyle', { fill: { fill: val } });
    }
  });

  /** 描边颜色 v-model */
  const strokeColorModel = computed<string>({
    get: () => {
      const color = props.textStyle?.stroke?.color;
      if (!color) return '#467AFF';
      return typeof color === 'string' ? toHexColor(color) : toHexColor(color);
    },
    set: (val) => {
      if (val)
        emit('update:textStyle', {
          stroke: { color: val, width: props.textStyle?.stroke?.width ?? 2, alpha: props.textStyle?.stroke?.alpha ?? 1, join: props.textStyle?.stroke?.join, miterLimit: props.textStyle?.stroke?.miterLimit }
        });
    }
  });

  /** 描边不透明度 (0-1 -> 0-100% 展示) */
  const strokeAlpha = computed(() => {
    return Math.round((props.textStyle?.stroke?.alpha ?? 1) * 100);
  });

  function onStrokeAlphaInput(val: string) {
    const num = parseInt(val, 10);
    if (!isNaN(num)) {
      const s = props.textStyle?.stroke;
      emit('update:textStyle', {
        stroke: { color: s?.color ?? '#467AFF', width: s?.width ?? 2, alpha: Math.max(0, Math.min(100, num)) / 100, join: s?.join, miterLimit: s?.miterLimit }
      });
    }
  }

  // ============================================================
  // 描边粗细
  // ============================================================

  const strokeWidth = computed(() => props.textStyle?.stroke?.width ?? 0);

  function emitStrokeWidth(width: number) {
    const s = props.textStyle?.stroke;
    emit('update:textStyle', {
      stroke: { color: s?.color ?? '#467AFF', width, alpha: s?.alpha ?? 1, join: s?.join, miterLimit: s?.miterLimit }
    });
  }

  function onStrokeWidthInput(val: string) {
    const num = parseFloat(val);
    if (!isNaN(num)) {
      emitStrokeWidth(Math.max(0, Math.min(20, num)));
    }
  }

  function onStrokeWidthBlur(e: FocusEvent) {
    const input = e.target as HTMLInputElement;
    if (input.value.trim() === '' || isNaN(parseFloat(input.value))) {
      input.value = '0';
      emitStrokeWidth(0);
    }
  }

  // ============================================================
  // 阴影
  // ============================================================

  const DEG_TO_RAD = Math.PI / 180;

  const SHADOW_DEFAULTS = {
    alpha: 0.5,
    angle: 45 * DEG_TO_RAD, // 45度转弧度
    blur: 4,
    color: '#000000',
    distance: 2
  };

  const shadowEnabled = computed(() => !!props.textStyle?.shadow);

  function toggleShadow() {
    if (shadowEnabled.value) {
      emit('update:textStyle', { shadow: null as unknown as undefined });
    } else {
      emit('update:textStyle', { shadow: { ...SHADOW_DEFAULTS } });
    }
  }

  /** 阴影颜色 v-model */
  const shadowColorModel = computed<string>({
    get: () => {
      const color = props.textStyle?.shadow?.color;
      if (!color) return '#000000';
      return typeof color === 'string' ? toHexColor(color) : toHexColor(color);
    },
    set: (val) => {
      if (val) {
        emit('update:textStyle', {
          shadow: { ...(props.textStyle?.shadow ?? SHADOW_DEFAULTS), color: val }
        });
      }
    }
  });

  /** 阴影模糊 */
  const shadowBlur = computed(() => props.textStyle?.shadow?.blur ?? 0);

  function emitShadowUpdate(
    partial: Partial<{ blur: number; distance: number; angle: number; alpha: number }>
  ) {
    emit('update:textStyle', {
      shadow: { ...(props.textStyle?.shadow ?? SHADOW_DEFAULTS), ...partial }
    });
  }

  /** 阴影不透明度 (0-100% -> 0-1) */
  function onShadowAlphaInput(val: string) {
    const num = parseInt(val, 10);
    if (!isNaN(num)) {
      emitShadowUpdate({ alpha: Math.max(0, Math.min(100, num)) / 100 });
    }
  }

  function onShadowBlurInput(val: string) {
    const num = parseFloat(val);
    if (!isNaN(num)) {
      emitShadowUpdate({ blur: Math.max(0, Math.min(50, num)) });
    }
  }

  function onShadowBlurBlur(e: FocusEvent) {
    const input = e.target as HTMLInputElement;
    if (input.value.trim() === '' || isNaN(parseFloat(input.value))) {
      input.value = '0';
      emitShadowUpdate({ blur: 0 });
    }
  }

  /** 阴影距离 */
  const shadowDistance = computed(() => props.textStyle?.shadow?.distance ?? 0);

  function onShadowDistanceInput(val: string) {
    const num = parseFloat(val);
    if (!isNaN(num)) {
      emitShadowUpdate({ distance: Math.max(0, Math.min(50, num)) });
    }
  }

  function onShadowDistanceBlur(e: FocusEvent) {
    const input = e.target as HTMLInputElement;
    if (input.value.trim() === '' || isNaN(parseFloat(input.value))) {
      input.value = '0';
      emitShadowUpdate({ distance: 0 });
    }
  }

  /** 阴影角度 (存储用弧度，展示用度) */
  const shadowAngle = computed(() => {
    const rad = props.textStyle?.shadow?.angle ?? 0;
    return Math.round(rad * (180 / Math.PI));
  });

  function onShadowAngleInput(val: string) {
    const num = parseFloat(val);
    if (!isNaN(num)) {
      const deg = Math.max(0, Math.min(360, num));
      emitShadowUpdate({ angle: deg * DEG_TO_RAD });
    }
  }

  function onShadowAngleBlur(e: FocusEvent) {
    const input = e.target as HTMLInputElement;
    if (input.value.trim() === '' || isNaN(parseFloat(input.value))) {
      input.value = '0';
      emitShadowUpdate({ angle: 0 });
    }
  }

  // ============================================================
  // 拖拽调节值（按住图标左右拖动修改数值）
  // ============================================================

  interface ScrubConfig {
    sensitivity?: number; // 每1px对应多少值变化
    min?: number;
    max?: number;
    onDelta: (val: number) => void;
    debounceDelay?: number;
  }

  function createScrub(config: ScrubConfig) {
    const { sensitivity = 0.5, min = 0, max, onDelta } = config;
    let startX = 0;
    let currentValue = 0;
    let dragging = false;

    function onMouseDown(e: MouseEvent, baseValue: number) {
      e.preventDefault();
      dragging = true;
      startX = e.clientX;
      currentValue = baseValue;
      document.body.style.cursor = 'ew-resize';
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    }

    function onMouseMove(e: MouseEvent) {
      if (!dragging) return;
      const dx = e.clientX - startX;
      let newValue = currentValue + dx * sensitivity;
      if (min !== undefined) newValue = Math.max(min, newValue);
      if (max !== undefined) newValue = Math.min(max, newValue);
      onDelta(newValue);
    }

    function onMouseUp() {
      dragging = false;
      document.body.style.cursor = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }

    return { onMouseDown };
  }

  // 描边粗细拖拽 (1-20, step 0.5)
  const strokeWidthScrub = createScrub({
    sensitivity: 0.5,
    max: 20,
    onDelta: (val) => emitStrokeWidth(Math.round(val * 2) / 2)
  });

  // 阴影距离拖拽 (1-50, step 0.5)
  const shadowDistanceScrub = createScrub({
    sensitivity: 0.5,
    max: 50,
    onDelta: (val) => emitShadowUpdate({ distance: Math.round(val * 2) / 2 })
  });

  // 阴影角度拖拽 (1-360, step 1)
  const shadowAngleScrub = createScrub({
    sensitivity: 1,
    max: 360,
    onDelta: (val) => emitShadowUpdate({ angle: Math.round(val) * DEG_TO_RAD })
  });

  // 阴影模糊拖拽 (1-50, step 0.5)
  const shadowBlurScrub = createScrub({
    sensitivity: 0.5,
    max: 50,
    onDelta: (val) => emitShadowUpdate({ blur: Math.round(val * 2) / 2 })
  });

  function onAlignClick(value: 'left' | 'center' | 'right' | 'justify') {
    emit('update:textStyle', { layout: { align: value } });
  }

  function onFontStyleClick(value: 'bold' | 'italic') {
    switch (value) {
      case 'bold':
        emit('update:textStyle', {
          font: { fontWeight: isFontStyleActive('bold') ? 'normal' : 'bold' }
        });
        break;
      case 'italic':
        emit('update:textStyle', {
          font: { fontStyle: isFontStyleActive('italic') ? 'normal' : 'italic' }
        });
        break;
    }
  }

  function isFontStyleActive(value: 'bold' | 'italic'): boolean {
    const font = props.textStyle?.font;
    switch (value) {
      case 'bold':
        return font?.fontWeight === 'bold';
      case 'italic':
        return font?.fontStyle === 'italic';
      default:
        return false;
    }
  }

  function onLeadingChange(val: string) {
    emit('update:textStyle', { layout: { leading: parseFloat(val) } });
  }

  function onLeadingInput(val: string) {
    const num = parseFloat(val);
    if (!isNaN(num)) {
      emit('update:textStyle', { layout: { leading: Math.max(0, Math.min(50, num)) } });
    }
  }

  function onLeadingBlur(e: FocusEvent) {
    const input = e.target as HTMLInputElement;
    if (input.value.trim() === '' || isNaN(parseFloat(input.value))) {
      input.value = '0';
      emit('update:textStyle', { layout: { leading: 0 } });
    }
  }

  function onLetterSpacingChange(val: string) {
    emit('update:textStyle', { layout: { letterSpacing: parseFloat(val) } });
  }

  function onLetterSpacingInput(val: string) {
    const num = parseFloat(val);
    if (!isNaN(num)) {
      emit('update:textStyle', { layout: { letterSpacing: Math.max(0, Math.min(50, num)) } });
    }
  }

  function onLetterSpacingBlur(e: FocusEvent) {
    const input = e.target as HTMLInputElement;
    if (input.value.trim() === '' || isNaN(parseFloat(input.value))) {
      input.value = '0';
      emit('update:textStyle', { layout: { letterSpacing: 0 } });
    }
  }

  function onTextInput(e: Event) {
    const target = e.target as HTMLTextAreaElement;
    emit('update:text', target.value);
  }

  function onFontSizeInput(val: number | string) {
    const num = typeof val === 'string' ? parseInt(val, 10) : val;
    if (!isNaN(num)) {
      emit('update:textStyle', {
        font: { fontSize: Math.max(FONT_SIZE_MIN, Math.min(FONT_SIZE_MAX, num)) }
      });
    }
  }

  function onFontSizeChange(delta: number) {
    const currentSize = props.textStyle?.font?.fontSize ?? DEFAULT_FONT_SIZE;
    const newSize = Math.max(
      FONT_SIZE_MIN,
      Math.min(FONT_SIZE_MAX, currentSize + delta * FONT_SIZE_STEP)
    );
    emit('update:textStyle', { font: { fontSize: newSize } });
  }

  function onStyleSelect(preset: StylePreset) {
    // 保留当前字体/布局属性，其余（填充/描边/阴影）由新样式替换
    const current = props.textStyle;
    const merged: TextStyleData = {
      font: current?.font,
      layout: current?.layout,
      // 显式传入 null 来清除不存在的视觉属性
      fill: preset.style.fill ?? (null as unknown as undefined),
      stroke: preset.style.stroke ?? (null as unknown as undefined),
      shadow: preset.style.shadow ?? (null as unknown as undefined)
    };
    emit('update:textStyle', merged);
  }
</script>

<style scoped>
  .text-props {
    width: 100%;
    box-sizing: border-box;
    padding: 16px 20px;
  }

  /* Text content box */
  .text-props__content-box {
    margin-top: 15px;
    border: 1.2px solid #e5e7eb;
    border-radius: 6px;
    padding: 12px;
    background: #fff;
    display: flex;
    flex-direction: column;
    gap: 10px;
    box-sizing: border-box;
  }

  .text-props__content-input {
    width: 100%;
    border: none;
    outline: none;
    resize: none;
    padding: 0;
    font-family: inherit;
    font-size: 14px;
    font-weight: 350;
    color: #333;
    line-height: 1.5;
    background: transparent;
    box-sizing: border-box;
    min-height: 63px;
  }

  .text-props__content-input::placeholder {
    color: #999;
  }

  .text-props__content-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .text-props__content-counter {
    font-size: 14px;
    font-weight: 350;
    color: #6e6a7c;
    opacity: 0.5;
    line-height: 1.5;
  }

  /* Font row */
  .text-props__font-row {
    margin-top: 15px;
  }

  /* Tool row */
  .text-props__tool-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
  }

  .text-props__color-swatch {
    width: 80px;
    height: 36px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 3px;
    box-sizing: border-box;
    cursor: pointer;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    background: #fff;
  }

  .text-props__color-swatch::after {
    content: '';
    display: block;
    width: 100%;
    height: 28px;
    border-radius: 5px;
    background-color: var(--swatch-color);
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
  }

  .text-props__color-swatch:hover {
    border-color: #6033e1;
  }

  .text-props__font-size {
    flex: 1;
    min-width: 112px;
    height: 36px;
  }

  .text-props__font-size :deep(.el-select__wrapper) {
    height: 36px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    box-shadow: none;
    font-weight: 500;
    font-size: 13px;
    color: #374151;
    padding: 0 12px;
    cursor: pointer;
  }

  .text-props__font-size :deep(.el-select__wrapper):hover,
  .text-props__font-size :deep(.el-select__wrapper.is-focus) {
    border-color: #6033e1;
    box-shadow: none;
  }

  .text-props__font-size :deep(.el-select__placeholder),
  .text-props__font-size :deep(.el-select__selected-item) {
    color: #374151;
  }

  .text-props__step-controls {
    display: flex;
    align-items: center;
    position: relative;
    width: 72px;
    height: 36px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
    flex-shrink: 0;
    box-sizing: border-box;
  }

  .text-props__step-divider {
    position: absolute;
    left: 50%;
    width: 1px;
    height: 34px;
    background-color: #e2e8f0;
  }

  .text-props__step-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    height: 100%;
    border: none;
    background: #fff;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .text-props__step-btn:hover {
    background-color: #f1f5f9;
  }

  .text-props__align-row {
    display: flex;
    align-items: center;
    gap: 19px;
    margin-top: 16px;
  }

  .text-props__align-row .text-props__align-group:first-child {
    flex: 3;
  }

  .text-props__align-row .text-props__align-group:last-child {
    flex: 4;
  }

  .text-props__align-group {
    display: flex;
    height: 36px;
    border: 1px solid #e2e8f0;
    border-radius: 5.5px;
    overflow: hidden;
  }

  .text-props__align-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-width: 36px;
    height: 100%;
    border: none;
    background: #fff;
    cursor: pointer;
    transition: background-color 0.2s;
    position: relative;
  }

  .text-props__align-btn:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 0;
    top: 1px;
    bottom: 1px;
    width: 1px;
    background-color: #e2e8f0;
  }

  .text-props__align-btn:hover {
    background-color: #f1f5f9;
  }

  .text-props__align-btn--active {
    background-color: #fbf9ff;
  }

  .text-props__align-btn--active:hover {
    background-color: #fbf9ff;
  }

  /* Separator */
  .text-props__sep {
    height: 1px;
    background-color: #eee;
    margin-top: 16px;
  }

  /* Spacing */
  .text-props__spacing-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
  }

  .text-props__label {
    font-weight: 500;
    font-size: 13px;
    line-height: 14px;
    color: #616569;
    white-space: nowrap;
    flex-shrink: 0;
    width: 52px;
  }

  .text-props__slider-wrap {
    flex: 1;
    display: flex;
    align-items: center;
  }

  .text-props__slider {
    width: 100%;
    height: 4px;
    -webkit-appearance: none;
    appearance: none;
    background: #ede8ff;
    border-radius: 2px;
    outline: none;
    cursor: pointer;
  }

  .text-props__slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #6033e1;
    cursor: pointer;
    border: 2px solid #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  .text-props__slider::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #6033e1;
    cursor: pointer;
    border: 2px solid #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  .text-props__spacing-val {
    width: 56px;
    height: 24px;
    padding: 0 8px;
    background-color: rgba(239, 240, 247, 0.5);
    border: none;
    border-radius: 4px;
    font-size: 12px;
    font-family: inherit;
    color: #333;
    text-align: center;
    outline: none;
    box-sizing: border-box;
    -moz-appearance: textfield;
    appearance: textfield;
  }

  .text-props__spacing-val::-webkit-inner-spin-button,
  .text-props__spacing-val::-webkit-outer-spin-button {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
  }

  .text-props__spacing-val:hover,
  .text-props__spacing-val:focus {
    background-color: rgba(239, 240, 247, 0.8);
  }

  /* Style preview */
  .text-props__preview {
    background-color: rgba(239, 240, 247, 0.5);
    border: 1px solid transparent;
    border-radius: 6px;
    padding: 12px 29px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
    cursor: pointer;
    user-select: none;
    transition:
      background-color 0.2s,
      border-color 0.2s,
      box-shadow 0.2s;
  }

  .text-props__preview:hover {
    background-color: rgba(239, 240, 247, 0.75);
    border-color: rgba(96, 51, 225, 0.25);
    box-shadow: 0 2px 8px rgba(96, 51, 225, 0.08);
  }

  .text-props__preview:active {
    background-color: rgba(239, 240, 247, 0.9);
    box-shadow: 0 1px 4px rgba(96, 51, 225, 0.06);
  }

  .text-props__preview-left {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .text-props__preview-label {
    font-weight: 400;
    font-size: 13px;
    line-height: 20px;
    color: #160211;
  }

  .text-props__preview-chevron {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }

  .text-props__preview:hover .text-props__preview-chevron {
    transform: translateX(2px);
  }

  .text-props__preview-char {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 26px;
    flex-shrink: 0;
  }

  .text-props__big-a {
    font-weight: 900;
    font-size: 25px;
    line-height: 26px;
    color: #1e293b;
    text-align: center;
  }

  /* Color rows */
  .text-props__color-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 18px;
  }

  .text-props__color-label {
    font-weight: 400;
    font-size: 13px;
    line-height: 13px;
    color: #616569;
  }

  .text-props__color-option {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 155px;
    height: 36px;
    padding: 0 10px;
    background-color: rgba(239, 240, 247, 0.5);
    border-radius: 6px;
    cursor: pointer;
    box-sizing: border-box;
    transition: background-color 0.2s;
  }

  .text-props__color-option:hover {
    background-color: rgba(239, 240, 247, 0.8);
  }

  .text-props__color-option span {
    font-weight: 400;
    font-size: 13px;
    color: #616569;
  }

  /* Stroke group (color + width inline, matching Figma) */
  .text-props__stroke-group {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .text-props__stroke-color-box {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 92px;
    height: 28px;
    padding: 0 8px;
    background-color: rgba(239, 240, 247, 0.5);
    border-radius: 6px;
    cursor: pointer;
    box-sizing: border-box;
    transition: background-color 0.2s;
  }

  .text-props__stroke-color-box:hover {
    background-color: rgba(239, 240, 247, 0.8);
  }

  .text-props__stroke-color-box span {
    font-weight: 400;
    font-size: 12px;
    color: #616569;
  }

  .text-props__stroke-width-box {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 56px;
    height: 28px;
    padding: 0 8px;
    background-color: #f7f8fb;
    border-radius: 6px;
    box-sizing: border-box;
  }

  .text-props__stroke-width-input {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: none;
    outline: none;
    background: transparent;
    font-size: 14px;
    font-family: Inter, sans-serif;
    color: #616569;
    text-align: left;
    -moz-appearance: textfield;
    appearance: textfield;
    padding: 0;
  }

  .text-props__stroke-width-input::-webkit-inner-spin-button,
  .text-props__stroke-width-input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
  }

  .text-props__gradient-preview {
    width: 16px;
    height: 19px;
    border-radius: 4px;
    border: 1px solid rgba(221, 13, 49, 0.2);
    flex-shrink: 0;
  }

  .text-props__color-dot {
    width: 16px;
    height: 19px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    flex-shrink: 0;
  }

  /* Apply button */
  .text-props__apply-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    height: 46px;
    margin-top: 20px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background-color: #fff;
    cursor: pointer;
    transition:
      background-color 0.2s,
      border-color 0.2s;
  }

  .text-props__apply-btn:hover {
    background-color: #f8fafc;
    border-color: #6033e1;
  }

  .text-props__apply-btn:active {
    opacity: 0.8;
  }

  .text-props__apply-btn span {
    font-weight: 400;
    font-size: 13px;
    line-height: 19.5px;
    color: #374151;
  }

  /* Shadow header row */
  .text-props__shadow-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 18px;
  }

  .text-props__shadow-label {
    font-weight: 400;
    font-size: 13px;
    line-height: 13px;
    color: #616569;
  }

  /* Toggle switch (30×16 pill, matching Figma) */
  .text-props__toggle {
    width: 30px;
    height: 16px;
    border-radius: 12px;
    border: none;
    background: #d1d5db;
    cursor: pointer;
    position: relative;
    padding: 0;
    transition: background-color 0.2s;
    flex-shrink: 0;
  }

  .text-props__toggle--on {
    background: #6033e1;
  }

  .text-props__toggle::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #fff;
    transition: left 0.2s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  }

  .text-props__toggle--on::after {
    left: 16px;
  }

  /* Shadow settings card (matching Figma ShadowPopover) */
  .text-props__shadow-box {
    border: 1px solid #eff0f7;
    border-radius: 10px;
    padding: 10px 12px;
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  /* Shadow row: label left, controls right */
  .text-props__shadow-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 28px;
  }

  .text-props__shadow-label {
    font-weight: 400;
    font-size: 12px;
    color: #616569;
    flex-shrink: 0;
  }

  .text-props__shadow-row-right {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  /* Shadow color box (110px, matching Figma) */
  .text-props__shadow-color-box {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 110px;
    height: 28px;
    padding: 0 9px;
    background-color: rgba(239, 240, 247, 0.5);
    border-radius: 6px;
    cursor: pointer;
    box-sizing: border-box;
    transition: background-color 0.2s;
  }

  .text-props__shadow-color-box:hover {
    background-color: rgba(239, 240, 247, 0.8);
  }

  .text-props__shadow-color-box span {
    font-weight: 400;
    font-size: 13px;
    color: #616569;
  }

  /* Shadow number input box (62px, matching Figma NumInput) */
  .text-props__shadow-num-icon,
  .text-props__scrub-icon {
    flex-shrink: 0;
    cursor: ew-resize;
  }

  .text-props__shadow-num-icon {
    opacity: 0.5;
  }

  .text-props__shadow-num-box {
    display: flex;
    align-items: center;
    gap: 2px;
    width: 62px;
    height: 28px;
    padding: 0 8px;
    background-color: #f7f8fb;
    border-radius: 6px;
    box-sizing: border-box;
  }

  .text-props__shadow-num-input {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    font-family: Inter, sans-serif;
    font-weight: 400;
    color: #616569;
    text-align: center;
    -moz-appearance: textfield;
    appearance: textfield;
    padding: 0;
  }

  .text-props__shadow-num-input::-webkit-inner-spin-button,
  .text-props__shadow-num-input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
  }

  .text-props__shadow-num-unit {
    font-size: 13px;
    font-family: Inter, sans-serif;
    font-weight: 400;
    color: #616569;
    flex-shrink: 0;
  }

  /* Disabled */
  .text-props--disabled {
    opacity: 0.4;
    pointer-events: none;
    user-select: none;
  }
</style>

<style>
  .text-props__font-size-popper .el-popper__arrow {
    display: none !important;
  }
</style>
